import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Input } from "@shared/ui/input";
import {
  Users,
  MapPin,
  Calendar,
  User,
  Zap,
  Search,
  Loader2,
  CheckCircle,
  Star,
} from "lucide-react";

import { BulkAssignDialogProps } from "@features/wx/dispatcher/types";
import { TechnicianAssignmentModal } from "../assignment/technician/TechnicianAssignmentModal";

// Updated BulkAssignDialog with fixed scrolling
export const BulkAssignDialog: React.FC<BulkAssignDialogProps> = ({
  open,
  onOpenChange,
  selectedOrders,
  selectedOrdersData = [],
  sopData = [],
  onComplete,
}) => {
  const [showTechnicianModal, setShowTechnicianModal] = useState(false);

  // Mock data - replace with real data from your context/props
  const selectedOrderDetails = React?.useMemo(() => {
    // This should come from your parent component's selected orders data
    return selectedOrdersData?.map((sop) => ({
      id: sop?.id,
      name: sop?.name,
      area: sop.areaName,
      serviceAssociation: sop?.serviceAssociation,
      priority: sop?.priorityDisplay,
      status: sop?.statusDisplay,
      serviceCount: Math.floor(Math.random() * 5) + 1,
      sopId: sop?.soId, // Add SOP ID for API calls
    }));
  }, [selectedOrdersData]);

  console.log("selectedOrdersData", selectedOrdersData);

  const totalServices = selectedOrderDetails.reduce(
    (sum, order) => sum + order.serviceCount,
    0
  );

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "medium":
        return "bg-blue-100 text-blue-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="border-b pb-4 flex-shrink-0">
            <DialogTitle className="text-xl font-semibold">
              Bulk Assignment Preview
            </DialogTitle>
          </DialogHeader>

          {/* FIXED: Added flex flex-col to create proper flex container */}
          <div className="flex-1 min-h-0 overflow-hidden p-6 flex flex-col">
            {/* Summary Section */}
            <div className="mb-6 flex-shrink-0">
              <h3 className="font-medium mb-3">Summary</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Service Orders</p>
                      <p className="text-lg font-bold">
                        {selectedOrders.length}
                      </p>
                    </div>
                    <User className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Service Orders - FIXED: Better height management */}
            <div className="flex-1 flex flex-col min-h-0">
              <h3 className="font-medium mb-3 flex-shrink-0">
                Selected Service Orders
              </h3>

              {/* FIXED: Added min-h-0 to prevent flex item from growing beyond container */}
              <div className="flex-1 overflow-y-auto space-y-3 pr-2 min-h-0">
                {selectedOrderDetails.map((order) => (
                  <div
                    key={order.id}
                    className="p-3 rounded-lg border border-gray-200 bg-white flex-shrink-0"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-blue-600 text-sm">
                            {order.sopId}
                          </span>
                          <Badge className="bg-blue-100 text-blue-800">
                            {order.serviceAssociation}
                          </Badge>
                        </div>
                        <h4 className="font-medium text-gray-900 text-sm">
                          {order.name}
                        </h4>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge className={getPriorityColor(order.priority)}>
                          {order.priority}
                        </Badge>
                      </div>
                    </div>

                    {/* <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600">
                          {order.serviceCount} services
                        </span>
                      </div>
                    </div> */}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4 flex-shrink-0">
            <div className="flex gap-3 w-full justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => setShowTechnicianModal(true)}
                disabled={selectedOrders.length === 0}
                className="flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                Assign Technician
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TechnicianAssignmentModal
        open={showTechnicianModal}
        onOpenChange={setShowTechnicianModal}
        selectedOrders={selectedOrders}
        selectedOrdersData={selectedOrderDetails}
        sop={selectedOrdersData}
        onComplete={onComplete}
      />
    </>
  );
};
